import { ComponentPropsWithoutRef, forwardRef } from 'react';
import { cn } from '@v2/shared/lib/utils';
import FormField from './FormField';
import { controlClasses } from './controlClasses';

type Props = ComponentPropsWithoutRef<'select'> & {
  label?: string;
  error?: string;
};

/**
 * FormInput's `<select>` counterpart, on the same controlClasses so a dropdown and a text
 * field cannot read as two different controls sitting next to each other.
 *
 * The height floor is the one thing it needs that the others do not. What makes an input
 * 52px tall is its line-height; a `<select>` never gets one, because Chrome's UA
 * stylesheet sets `line-height: normal !important` on a closed select and no author
 * declaration reaches it — verified in the browser, an inline `line-height` leaves the
 * computed value at `normal` and the box 6px short. So the box is given the height
 * instead: 3.25rem is controlClasses' own `p-3` and `border-2` around `leading-6`, which
 * is what an input beside it resolves to.
 *
 * A real `<select>` rather than shared/components/Select: that one is a headless Listbox
 * whose selected row is styled in Tailwind's red rather than the brand's, and it posts
 * nothing without JavaScript.
 */
const FormSelect = forwardRef<HTMLSelectElement, Props>(
  ({ label, id, error, className, children, ...props }, ref) => (
    <FormField id={id} label={label} error={error}>
      <select
        ref={ref}
        id={id}
        aria-invalid={error ? true : undefined}
        aria-describedby={error && id ? `${id}-error` : undefined}
        className={controlClasses(!!error, cn('min-h-[3.25rem] bg-white', className))}
        {...props}
      >
        {children}
      </select>
    </FormField>
  )
);

FormSelect.displayName = 'FormSelect';

export default FormSelect;
